<template>
  <div class="m-b-15">
    <div class="inline-block date-selector">
      <template v-if="dateCreatedRange.data !== null">
        <span class="c-gray">{{ $t('Date created') }}</span>
        <br />
        <InputDateRange v-model:date-range="dateCreatedRange.data" format="YYYY-MM" />
      </template>

      <a v-else class="btn btn-sm btn-default" @click="addDate(dateCreatedRange)">
        {{ $t('Filter by created date') }}
      </a>
    </div>
    <div class="inline-block date-selector">
      <template v-if="dateStartRange.data !== null">
        <span class="c-gray">{{ $t('Contract start date') }}</span>
        <br />
        <InputDateRange v-model:date-range="dateStartRange.data" format="YYYY-MM" />
      </template>
      <a v-else class="btn btn-sm btn-default" @click="addDate(dateStartRange)">
        {{ $t('Filter by contract start date') }}
      </a>
    </div>
    <WdsStack align-center gap-sm justify-start wrap>
      <WdsSelect
        v-model="contractStatus.data"
        :list="$options.contractStatusOptions"
        :placeholder="$t('Contract status')"
        multi-select
        can-deselect
        max-labels-visible="1"
        labels-primary
      >
        <template #select-all-text>
          <WdsText :text="$t('Select all')" />
        </template>
      </WdsSelect>
      <WdsSelect
        v-model="billsSelection.data"
        :list="$options.billingOptions"
        :placeholder="$t('Billing status')"
        multi-select
        can-deselect
        max-labels-visible="1"
        labels-primary
      >
        <template #select-all-text>
          <WdsText :text="$t('Select all')" />
        </template>
      </WdsSelect>
      <WdsSelect
        v-if="teamMembers.length"
        v-model="selectedOwnerObjects"
        :list="teamMemberOptions"
        :placeholder="$t('All job owners')"
        multi-select
        can-deselect
        max-labels-visible="1"
        labels-primary
      >
        <template #select-all-text>
          <WdsText :text="$t('Select all')" />
        </template>
      </WdsSelect>
      <WdsSelect
        v-if="recruiters.length"
        v-model="selectedRecruiterObjects"
        :list="recruiterOptions"
        :placeholder="$t('All recruiters')"
        multi-select
        can-deselect
        max-labels-visible="1"
        labels-primary
      >
        <template #select-all-text>
          <WdsText :text="$t('Select all')" />
        </template>
      </WdsSelect>
      <WdsSelect
        v-model="contractType.data"
        :list="formattedContractOptions"
        :placeholder="$t('All contract types')"
        multi-select
        can-deselect
        max-labels-visible="1"
        labels-primary
      >
        <template #select-all-text>
          <WdsText :text="$t('Select all')" />
        </template>
      </WdsSelect>
      <WdsButtonGhost secondary class="tw-h-[38px]" @click="resetFilters">
        <div class="tw-my-auto">
          {{ $t('Reset filters') }}
        </div>
      </WdsButtonGhost>
    </WdsStack>
  </div>
</template>

<script>
import { WdsSelect, WdsStack, WdsButtonGhost, WdsText } from '@worksome/wds-components'
import DateRange from '@/vue-prop-types/DateRange'
import InputDateRange from '@/components/InputDateRange.vue'
import { LocalStorageEncoder } from '@/utils/Encoders'

export default {
  billingOptions: [
    { value: 1, label: $t('Has bills') },
    { value: 0, label: $t('Has no bills') },
  ],
  contractStatusOptions: [
    { label: $t('Not accepted'), value: 'NOT_ACCEPTED' },
    { label: $t('Active'), value: 'ACTIVE' },
    { label: $t('Ended'), value: 'ENDED' },
  ],
  components: {
    WdsText,
    WdsSelect,
    WdsStack,
    WdsButtonGhost,
    InputDateRange,
  },
  props: {
    teamMembers: {
      type: Array,
      default: () => [],
    },
    recruiters: {
      type: Array,
      default: () => [],
    },
    contractTypeOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isResetting: false,
      dateCreatedRange: new LocalStorageEncoder({
        storageKey: 'created_YYYY-MM',
        decoder: (dateStr) =>
          DateRange.fromString({
            str: dateStr,
            format: 'YYYY-MM',
            fallback: null,
            unsafe: true,
          }),
        encoder: (date) => (date !== null ? `${date.start},${date.end}` : ''),
        defaultVal: null,
      }),
      dateStartRange: new LocalStorageEncoder({
        storageKey: 'start-date_YYYY-MM',
        decoder: (dateStr) =>
          DateRange.fromString({
            str: dateStr,
            format: 'YYYY-MM',
            fallback: null,
            unsafe: true,
          }),
        encoder: (date) => (date !== null ? `${date.start},${date.end}` : ''),
        defaultVal: null,
      }),
      contractStatus: new LocalStorageEncoder({
        storageKey: 'contract-status',
        decoder: this.decodeStatusOptionsFromUrl, // get from uri
        filter: (statuses) => statuses.map((status) => status.value), // pushing to uri
        defaultVal: null,
      }),
      contractType: new LocalStorageEncoder({
        storageKey: 'contract-type',
        decoder: this.decodeContractTypesOptionsFromUrl, // get from uri
        filter: (types) => types.map((type) => type.value), // pushing to uri
        defaultVal: null,
      }),
      billsSelection: new LocalStorageEncoder({
        storageKey: 'bills',
        decoder: this.decodeBillingOptionsFromUrl, // get from uri
        filter: (statuses) => statuses.map((status) => status.value), // pushing to uri
        defaultVal: null,
      }),
      selectedOwnerIds: new LocalStorageEncoder({
        storageKey: 'owners',
        decoder: (data) =>
          data
            ?.split(',')
            .filter((val) => val !== '')
            .map((val) => (val === 'NO_OWNER' ? val : Number(val))) || [],
        defaultVal: [],
      }),
      selectedRecruiterIds: new LocalStorageEncoder({
        storageKey: 'recruiters',
        decoder: (data) =>
          data?.split(',').map((val) => (val === 'NO_RECRUITER' ? val : Number(val))) || [],
        defaultVal: [],
      }),
    }
  },
  computed: {
    filter() {
      const createdFilterRange =
        this.dateCreatedRange.data !== null
          ? DateRange.convertToFormat(this.dateCreatedRange.data, 'YYYY-MM-DD')
          : ''
      const startFilterRange =
        this.dateStartRange.data !== null
          ? DateRange.convertToFormat(this.dateStartRange.data, 'YYYY-MM-DD')
          : ''
      return {
        hasBills: this.hasBills,
        dateCreatedFrom: createdFilterRange.start === '' ? undefined : createdFilterRange.start,
        dateCreatedTo: createdFilterRange.end === '' ? undefined : createdFilterRange.end,
        dateStartFrom: startFilterRange.start === '' ? undefined : startFilterRange.start,
        dateStartTo: startFilterRange.end === '' ? undefined : startFilterRange.end,
        contractStatus: this.contractStatus.data.map((status) => status.value),
        contractType: this.contractType.data.map((type) => type.value),
        // exclude any owners not contained in the options
        owners: this.selectedOwnerIds.data.filter(
          (value) =>
            value !== 'NO_OWNER' &&
            this.teamMemberOptions.map((teamMember) => teamMember.value).includes(value),
        ),
        noOwners: this.selectedOwnerIds.data.some((value) => value === 'NO_OWNER'),
        // exclude any recruiters not contained in the options
        recruiters: this.selectedRecruiterIds.data.filter(
          (value) =>
            value !== 'NO_RECRUITER' &&
            this.recruiterOptions.map((recruiters) => recruiters.value).includes(value),
        ),
        noRecruiters: this.selectedRecruiterIds.data.some((value) => value === 'NO_RECRUITER'),
      }
    },
    hasBills() {
      const billsSelection = this.billsSelection.data.map((selection) => selection.value)
      if (billsSelection.length < 1 || (billsSelection.includes(1) && billsSelection.includes(0))) {
        return null
      }
      return !!billsSelection.includes(1)
    },
    selectedOwnerObjects: {
      get() {
        return this.teamMemberOptions.filter((teamMember) =>
          this.selectedOwnerIds.data.includes(teamMember.value),
        )
      },
      set(newValue) {
        this.selectedOwnerIds.data = newValue.map((selectedOwner) => selectedOwner.value)
      },
    },
    formattedTeamMemberOptions() {
      return this.teamMembers.map((teamMember) => ({
        value: teamMember.id,
        label: teamMember.name,
      }))
    },
    teamMemberOptions() {
      return this.formattedTeamMemberOptions.length
        ? [{ value: 'NO_OWNER', label: $t('Unassigned') }, ...this.formattedTeamMemberOptions]
        : []
    },
    selectedRecruiterObjects: {
      get() {
        return this.recruiterOptions.filter((recruiter) =>
          this.selectedRecruiterIds.data.includes(recruiter.value),
        )
      },
      set(newValue) {
        this.selectedRecruiterIds.data = newValue.map(
          (selectedRecruiter) => selectedRecruiter.value,
        )
      },
    },
    formattedRecruiterOptions() {
      return this.recruiters.map((recruiter) => ({
        value: recruiter.id,
        label: recruiter.name,
      }))
    },
    recruiterOptions() {
      return this.formattedRecruiterOptions.length
        ? [{ value: 'NO_RECRUITER', label: $t('No recruiter') }, ...this.formattedRecruiterOptions]
        : []
    },
    formattedContractOptions() {
      return this.contractTypeOptions.map((contractType) => ({
        value: contractType.enum,
        label: contractType.name,
      }))
    },
  },
  watch: {
    filter: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.$emit('filters-updated', newVal)
      },
    },
  },
  methods: {
    decodeStatusOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.$options.contractStatusOptions.filter((option) =>
        selections.some((selection) => selection === option.value),
      )
    },
    decodeContractTypesOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.contractTypeOptions
        .map((type) => ({
          value: type.enum,
          label: type.name,
        }))
        .filter((option) => selections.some((selection) => selection === option.value))
    },
    decodeBillingOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.$options.billingOptions.filter((option) =>
        selections.some((selection) => parseInt(selection, 10) === option.value),
      )
    },
    resetFilters() {
      this.isResetting = true
      setTimeout(() => {
        this.isResetting = false
      }, 600)
      this.dateCreatedRange.data = null
      this.dateStartRange.data = null
      this.billsSelection.data = []
      this.selectedOwnerIds.data = []
      this.selectedRecruiterIds.data = []
      this.contractStatus.data = []
      this.contractType.data = []
    },
    addDate(date) {
      // eslint-disable-next-line no-param-reassign -- Baseline eslint ignore please fix if working on this
      date.data = new DateRange({ format: 'YYYY-MM', unsafe: true })
    },
  },
}
</script>

<style scoped>
.inline-block {
  display: inline-block !important;
}

.date-selector {
  padding: 5px 5px 5px 0;
}

@-webkit-keyframes rotation {
  from {
    -webkit-transform: rotate(359deg);
  }
  to {
    -webkit-transform: rotate(0deg);
  }
}

.fa-undo.loading {
  -webkit-animation: rotation 0.3s 1 ease-out;
}
</style>
